import { BOARD_SIZE } from '../constants';

// Check if a position is within board bounds
export const isWithinBounds = (row, col) => {
  return row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE;
};

// Check if there's a wall between two adjacent positions
export const hasWallBetween = (walls, fromRow, fromCol, toRow, toCol) => {
  if (fromRow === toRow) {
    // Moving left or right (horizontal movement)
    // Check vertical wall between columns
    const col = Math.max(fromCol, toCol); // vertical wall is to the right of the leftmost cell
    return walls.vertical[fromRow][col];
  } else if (fromCol === toCol) {
    // Moving up or down (vertical movement)
    // Check horizontal wall between rows
    const row = Math.max(fromRow, toRow); // horizontal wall is below the upper cell
    return walls.horizontal[row][fromCol];
  }
  return false;
};

// Check if a path is blocked
const isPathBlocked = (board, walls, path) => {
  const [sourceRow, sourceCol] = path[0];

  for (let i = 0; i < path.length - 1; i++) {
    const [row1, col1] = path[i];
    const [row2, col2] = path[i + 1];

    // Check if position is within bounds
    if (!isWithinBounds(row2, col2)) return true;

    // Check if there's a wall between positions
    if (hasWallBetween(walls, row1, col1, row2, col2)) return true;

    // Check if there's a stone at the position (except for source)
    if ((row2 !== sourceRow || col2 !== sourceCol) && board[row2][col2] !== null) return true;
  }
  return false;
};

// Get all possible moves for a stone
export const getPossibleMoves = (board, walls, row, col) => {
  const possibleMoves = new Set();

  // Helper to add a move if the path is not blocked
  const addMoveIfValid = (targetRow, targetCol, paths) => {
    // Check if any of the paths to the target is not blocked
    const isValid = paths.some(path => !isPathBlocked(board, walls, path));

    if (isValid) {
      possibleMoves.add(`${targetRow},${targetCol}`);
    }
  };

  // Staying in place
  addMoveIfValid(row, col, [
    [[row, col], [row + 1, col]],
    [[row, col], [row - 1, col]],
    [[row, col], [row, col + 1]],
    [[row, col], [row, col - 1]],
  ]);

  // Single step moves
  addMoveIfValid(row + 1, col, [[[row, col], [row + 1, col]]]);
  addMoveIfValid(row - 1, col, [[[row, col], [row - 1, col]]]);
  addMoveIfValid(row, col + 1, [[[row, col], [row, col + 1]]]);
  addMoveIfValid(row, col - 1, [[[row, col], [row, col - 1]]]);

  // Double step moves
  addMoveIfValid(row + 2, col, [[[row, col], [row + 1, col], [row + 2, col]]]);
  addMoveIfValid(row - 2, col, [[[row, col], [row - 1, col], [row - 2, col]]]);
  addMoveIfValid(row, col + 2, [[[row, col], [row, col + 1], [row, col + 2]]]);
  addMoveIfValid(row, col - 2, [[[row, col], [row, col - 1], [row, col - 2]]]);

  // Diagonal moves
  addMoveIfValid(row + 1, col + 1, [
    [[row, col], [row + 1, col], [row + 1, col + 1]],
    [[row, col], [row, col + 1], [row + 1, col + 1]]
  ]);
  addMoveIfValid(row - 1, col - 1, [
    [[row, col], [row - 1, col], [row - 1, col - 1]],
    [[row, col], [row, col - 1], [row - 1, col - 1]]
  ]);
  addMoveIfValid(row + 1, col - 1, [
    [[row, col], [row + 1, col], [row + 1, col - 1]],
    [[row, col], [row, col - 1], [row + 1, col - 1]]
  ]);
  addMoveIfValid(row - 1, col + 1, [
    [[row, col], [row - 1, col], [row - 1, col + 1]],
    [[row, col], [row, col + 1], [row - 1, col + 1]]
  ]);

  return Array.from(possibleMoves).map(pos => {
    const [r, c] = pos.split(',').map(Number);
    return { row: r, col: c };
  });
}; 
